import React, { useState } from 'react';
import { ArrowRight, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from './components/Navigation';
import SiteFooter from './components/SiteFooter';

const BILLING_OPTIONS = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'yearly', label: 'Yearly' },
];

const PRICING_TIERS = [
  {
    key: 'essentials',
    name: 'Essentials',
    kicker: 'Offline-first foundation',
    storageLabel: 'Local-only setup',
    focusDescription:
      'Budget-friendly access to TECNIQ’s full offline toolkit. Cloud backup and multi-device sync stay switched off.',
    prices: {
      monthly: { display: '$3.99', amount: 3.99, subtext: '/ month' },
      yearly: { display: '$39.99', amount: 39.99, subtext: '/ year' },
    },
    features: [
      { text: 'Unlimited Exercise Video Logs', positive: true },
      { text: 'Full Body Journal Access', positive: true },
      { text: 'Unlimited Custom Units and Routines', positive: true },
      { text: 'Advanced Analytics and Statistics', positive: true },
      { text: 'Goals Tracking and Progress Notification', positive: true },
      { text: 'Cloud Data Backup', positive: false },
      { text: 'Multi-Device Sync', positive: false },
    ],
  },
  {
    key: 'standard',
    name: 'Standard',
    kicker: 'Cloud-connected core',
    storageLabel: '10GB cloud backup',
    focusDescription:
      'The most popular tier, layering automatic cloud backup and multi-device sync onto the Essentials foundation.',
    featured: true,
    prices: {
      monthly: { display: '$5.99', amount: 5.99, subtext: '/ month' },
      yearly: { display: '$59.99', amount: 59.99, subtext: '/ year' },
    },
    features: [
      { text: 'Everything in Essentials', positive: true },
      { text: 'Automatic Cloud Data Backup up to 10GB', positive: true, pending: false },
      { text: 'Multi-Device Sync', positive: true, pending: false },
    ],
  },
  {
    key: 'plus',
    name: 'Plus',
    kicker: 'Expanded cloud',
    storageLabel: '50GB total cloud storage',
    focusDescription:
      'The premium tier for power users, keeping every Standard benefit while expanding cloud capacity for heavier usage.',
    cardClassName: 'pricing-card--premium',
    prices: {
      monthly: { display: '$14.99', amount: 14.99, subtext: '/ month' },
      yearly: { display: '$149.99', amount: 149.99, subtext: '/ year' },
    },
    features: [
      { text: 'Everything in Standard', positive: true },
      { text: 'Additional 40GB Cloud Storage', positive: true, pending: false },
    ],
  },
];

const formatAud = (amount) => `$${amount.toFixed(2)}`;

const getSavingsAmount = (monthlyAmount, yearlyAmount) => {
  const difference = monthlyAmount * 12 - yearlyAmount;
  return difference > 0 ? difference : 0;
};

const getTierPriceNote = (tier, billingCycle) => {
  const savingsAmount = getSavingsAmount(
    tier.prices.monthly.amount,
    tier.prices.yearly.amount,
  );

  if (billingCycle === 'yearly') {
    const parts = [`Equivalent to ${formatAud(tier.prices.yearly.amount / 12)} / month`];

    if (savingsAmount > 0) {
      parts.push(`Save ${formatAud(savingsAmount)}`);
    }

    return parts.join(' · ');
  }

  if (savingsAmount > 0) {
    return `Yearly plan saves ${formatAud(savingsAmount)}`;
  }

  return 'Billed monthly';
};

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState('yearly');

  const footerAside = (
    <div className="dispatch-card ghost-outline">
      <p className="spec-label">Pricing support</p>
      <h3 className="dispatch-card__title">Need a hand choosing the right tier?</h3>
      <p className="dispatch-card__body">
        Tell us whether you want a local-only setup or full cloud backup and sync, and we’ll
        point you to the best fit.
      </p>

      <div className="pricing-footer__actions">
        <a
          className="tk-button tk-button--primary tk-button--full"
          href="mailto:main@tecniq.com.au?subject=TECNIQ%20Pricing%20Question"
        >
          Email the team
          <ArrowRight size={18} />
        </a>

        <Link className="tk-button tk-button--secondary tk-button--full" to="/terms">
          Review billing terms
          <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );

  return (
    <div className="site-shell landing-page pricing-page">
      <Navigation />

      <main className="landing-main">
        <section className="landing-section pricing-hero" aria-labelledby="pricing-hero-heading">
          <div className="site-container pricing-hero__grid">
            <div className="pricing-hero__copy">
              <div className="hero-section__eyebrow pricing-hero__eyebrow">
                <span className="hero-section__pulse" aria-hidden="true" />
                <span className="spec-label">Subscription architecture</span>
              </div>

              <h1 className="pricing-hero__title" id="pricing-hero-heading">
                Choose the tier that fits your <em>training system.</em>
              </h1>
            </div>
          </div>



          <div className="site-container">
            <div className="site-container pricing-toggle-row">
                <div className="pricing-toggle" role="toolbar" aria-label="Billing cycle">
                {BILLING_OPTIONS.map((option) => (
                    <button
                    key={option.key}
                    className={`pricing-toggle__button ${billingCycle === option.key ? 'is-active' : ''}`}
                    type="button"
                    aria-pressed={billingCycle === option.key}
                    onClick={() => setBillingCycle(option.key)}
                    >
                    {option.label}
                    </button>
                ))}
                </div>
            </div>

            <div className="pricing-grid">
              {PRICING_TIERS.map((tier) => {
                const price = tier.prices[billingCycle];
                const cardClassName = [
                  'pricing-card',
                  tier.featured ? 'pricing-card--featured' : '',
                  tier.cardClassName || '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <article key={tier.key} className={cardClassName}>
                    <div className="pricing-card__top">
                      <div className="pricing-card__head">
                        <div>
                          <p className="pricing-card__kicker">{tier.kicker}</p>
                          <h3 className="pricing-card__title">{tier.name}</h3>
                        </div>

                        <div className="pricing-card__badge-row">
                          <span className="pricing-card__storage">{tier.storageLabel}</span>
                          {tier.featured ? (
                            <span className="pricing-card__badge">Most popular</span>
                          ) : null}
                        </div>
                      </div>

                      <p className="pricing-card__description">{tier.focusDescription}</p>
                    </div>

                    <div className="pricing-card__price-block">
                      <div className="pricing-card__price-row">
                        <p className="pricing-card__price-value">{price.display}</p>
                        <p className="pricing-card__price-subtext">{price.subtext}</p>
                      </div>

                      <p className="pricing-card__price-note">
                        {getTierPriceNote(tier, billingCycle)}
                      </p>
                    </div>

                    <ul className="pricing-card__features">
                      {tier.features.map((feature) => (
                        <li
                          key={`${tier.key}-${feature.text}`}
                          className={`pricing-card__feature ${feature.positive ? 'is-positive' : 'is-negative'}`}
                        >
                          <span className="pricing-card__feature-icon" aria-hidden="true">
                            {feature.positive ? <Check size={16} /> : <X size={16} />}
                          </span>

                          <div className="pricing-card__feature-copy">
                            <p>{feature.text}</p>
                            {feature.pending ? (
                              <span className="pricing-card__feature-status">Rolling out</span>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* <section className="landing-section landing-section--muted" aria-labelledby="pricing-compare-heading">
          <div className="site-container">
            <div className="section-heading section-heading--split">
              <div>
                <h2 className="section-display section-display--medium" id="pricing-compare-heading">
                  Compare the capability <em>at a glance.</em>
                </h2>
              </div>
            </div>

            <div className="pricing-matrix" role="list" aria-label="Pricing comparison matrix">
              <div className="pricing-matrix__header" aria-hidden="true">
                <span className="pricing-matrix__header-cell">Capability</span>
                {PRICING_TIERS.map((tier) => (
                  <span key={`header-${tier.key}`} className="pricing-matrix__header-cell">
                    {tier.name}
                  </span>
                ))}
              </div>

              {COMPARISON_ROWS.map((row) => (
                <article key={row.capability} className="pricing-matrix__row" role="listitem">
                  <div className="pricing-matrix__label">
                    <h3>{row.capability}</h3>
                    <p>{row.summary}</p>
                  </div>

                  <div className="pricing-matrix__values">
                    {PRICING_TIERS.map((tier) => {
                      const value = row.values[tier.key];

                      return (
                        <div
                          key={`${row.capability}-${tier.key}`}
                          className={`pricing-matrix__cell ${value.included ? 'is-included' : 'is-excluded'}`}
                        >
                          <p className="pricing-matrix__cell-plan">{tier.name}</p>

                          <span className="pricing-matrix__cell-status">
                            {value.included ? <Check size={16} /> : <X size={16} />}
                            {value.included ? 'Included' : 'Not included'}
                          </span>

                          <p className="pricing-matrix__cell-note">{value.note}</p>

                          {value.pending ? (
                            <span className="pricing-matrix__cell-status-badge">
                              Rolling out
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </article>
              ))}
            </div>

            <div className="pricing-disclaimer ghost-outline">
              <p className="spec-label">Billing note</p>
              <p>
                All prices are shown in AUD and reflect TECNIQ’s current subscription
                configuration. Subscriptions are billed through the Apple App Store or Google
                Play, renew automatically unless cancelled, and capabilities marked “Rolling out”
                reflect the current tier metadata as cloud services continue to expand.
              </p>
            </div>
          </div>
        </section> */}

        <section className="landing-cta" aria-labelledby="pricing-cta-heading">
          <div className="site-container landing-cta__inner">
            <h2 className="landing-cta__title" id="pricing-cta-heading">
              Need help choosing your <span>tier?</span>
            </h2>

            <div className="landing-cta__actions">
              <a href="#contact" className="tk-button tk-button--primary">
                Talk to the team
                <ArrowRight size={18} />
              </a>

              <Link to="/terms" className="tk-button tk-button--dark-outline">
                Read subscription terms
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        <div id="contact">
          <SiteFooter aside={footerAside} />
        </div>
      </main>
    </div>
  );
}
